import React from 'react';
import { format, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn, safeParseDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export function DateRangeBar({ 
    startDate, 
    endDate, 
    onStartDateChange, 
    onEndDateChange,
    onApply,
    className 
}) {
    const [fromOpen, setFromOpen] = React.useState(false);
    const [toOpen, setToOpen] = React.useState(false);

    // Safely parse dates using utility function
    const parsedStartDate = startDate ? safeParseDate(startDate) : null;
    const parsedEndDate = endDate ? safeParseDate(endDate) : null;
    
    // Format dates safely
    const formatDateSafe = (dateValue) => {
        if (!dateValue) return "Select date";
        const parsed = safeParseDate(dateValue);
        return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : "Select date";
    };

    const handleStartSelect = (date) => {
        onStartDateChange(date);
        setFromOpen(false);
        if (onApply) onApply();
    };

    const handleEndSelect = (date) => {
        onEndDateChange(date);
        setToOpen(false);
        if (onApply) onApply();
    };

    return (
        <div className={cn("flex flex-col sm:flex-row gap-4 p-4 bg-card rounded-lg border", className)}>
            {/* From Date */}
            <div className="flex-1">
                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                    From
                </label>
                <Popover open={fromOpen} onOpenChange={setFromOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="secondary"
                            className={cn(
                                "w-full justify-start text-left font-normal",
                                !startDate && "text-muted-foreground"
                            )}
                        >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formatDateSafe(startDate)}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                            mode="single"
                            selected={parsedStartDate && isValid(parsedStartDate) ? parsedStartDate : undefined}
                            onSelect={handleStartSelect}
                            initialFocus
                        />
                    </PopoverContent>
                </Popover>
            </div>

            {/* To Date */}
            <div className="flex-1">
                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                    To
                </label>
                <Popover open={toOpen} onOpenChange={setToOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="secondary"
                            className={cn(
                                "w-full justify-start text-left font-normal",
                                !endDate && "text-muted-foreground"
                            )}
                        >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formatDateSafe(endDate)}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                            mode="single"
                            selected={parsedEndDate && isValid(parsedEndDate) ? parsedEndDate : undefined}
                            onSelect={handleEndSelect}
                            initialFocus
                        />
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
}

export default DateRangeBar;

